/**
 * สรุปมาสาย — owner/HR/manager ดูสรุปเวลาพนักงานที่มาสาย (หน่วยนาที/ชั่วโมง)
 * ย้อนหลัง เลือกช่วงวันที่เองได้ (ไม่ผูกกับงวดเงินเดือน). คำนวณสดจาก
 * attendance_events ทุกครั้ง (ไม่อ่าน monthly_summary.late_minutes ซึ่งค้าง
 * จนกว่าจะกด "สรุปเวลา" ของงวดนั้น และมีแค่หน่วยเป็นเดือนปฏิทินเต็ม).
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, Download, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLateReport, type LateReportRow } from '../../hooks';
import { lateLabel } from '../../lib/late-format';
import { thisMonthRange, lastMonthRange, last7DaysRange, last30DaysRange } from '../../components/domain/AttendanceTimeline';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';
import { useAuthStore } from '../../lib/store';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

type Preset = 'this-month' | 'last-month' | 'last-7' | 'last-30';
const PRESETS: Array<{ id: Preset; label: string; range: () => { from: string; to: string } }> = [
  { id: 'this-month', label: 'เดือนนี้', range: thisMonthRange },
  { id: 'last-month', label: 'เดือนที่แล้ว', range: lastMonthRange },
  { id: 'last-7', label: '7 วัน', range: last7DaysRange },
  { id: 'last-30', label: '30 วัน', range: last30DaysRange },
];

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: 'Asia/Bangkok' }).format(
    new Date(`${iso}T00:00:00+07:00`),
  );
}

async function downloadCsv(path: string, filename: string, onError: (msg: string) => void) {
  const jwt = useAuthStore.getState().jwt;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    onError(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
  }
}

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3, 4].map((i) => (
        <tr key={i} className="border-b border-gray-50 last:border-0">
          <td className="px-3 py-2" colSpan={7}>
            <div className="h-4 bg-gray-100 rounded w-full animate-pulse" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function LateReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qFrom = searchParams.get('from');
  const qTo = searchParams.get('to');
  const initial = qFrom && qTo ? { from: qFrom, to: qTo } : thisMonthRange();
  const [range, setRangeState] = useState(initial);
  const [preset, setPreset] = useState<Preset | null>(qFrom && qTo ? null : 'this-month');
  const [showAll, setShowAll] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  function applyRange(next: { from: string; to: string }, p: Preset | null) {
    setRangeState(next);
    setPreset(p);
    setSearchParams({ from: next.from, to: next.to }, { replace: true });
  }

  function onDateInput(field: 'from' | 'to', value: string) {
    if (!value) return;
    applyRange({ ...range, [field]: value }, null);
  }

  const { data, isLoading, isError, refetch } = useLateReport(range);
  const rows: LateReportRow[] = (data?.rows ?? []).filter((r) => showAll || r.late_days > 0);

  return (
    <AdminLayout
      title="สรุปมาสาย"
      actions={
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="btn-secondary btn-slim inline-flex items-center gap-1.5"
            onClick={() =>
              void downloadCsv(
                `/hr/late-report.csv?from=${range.from}&to=${range.to}${showAll ? '&include_zero=1' : ''}`,
                `สรุปมาสาย-${range.from}_${range.to}.csv`,
                setDownloadError,
              )
            }
          >
            <Download className="w-4 h-4" /> สรุป (CSV)
          </button>
          <button
            type="button"
            className="btn-cta btn-slim inline-flex items-center gap-1.5"
            title="ดึงรายวัน: วันไหน/ใคร/สายกี่นาที/หักเท่าไหร่ (ตรงกับสลิป) เปิดใน Excel ได้"
            onClick={() =>
              void downloadCsv(
                `/hr/late-report/daily.csv?period=${range.to.slice(0, 7)}`,
                `หักมาสายรายวัน-${range.to.slice(0, 7)}.csv`,
                setDownloadError,
              )
            }
          >
            <Download className="w-4 h-4" /> หักมาสายรายวัน (Excel)
          </button>
        </div>
      }
    >
      {downloadError && <p className="text-xs text-red-600 mb-2">{downloadError}</p>}

      <div className="card p-3 mb-3">
        <div className="flex gap-1.5 mb-3 overflow-x-auto">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyRange(p.range(), p.id)}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                preset === p.id ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">ตั้งแต่</label>
            <input type="date" value={range.from} max={range.to} onChange={(e) => onDateInput('from', e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">ถึง</label>
            <input type="date" value={range.to} min={range.from} onChange={(e) => onDateInput('to', e.target.value)} className="input w-full" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> คนที่สาย</p>
          <p className="text-xl font-bold text-ink">{isLoading ? '-' : (data?.totals.employees_late ?? 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">รวมครั้ง</p>
          <p className="text-xl font-bold text-ink">{isLoading ? '-' : (data?.totals.late_days ?? 0)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> รวมเวลา</p>
          <p className="text-xl font-bold text-rose-700">{isLoading ? '-' : lateLabel(data?.totals.late_minutes ?? 0)}</p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
        แสดงคนไม่สายด้วย
      </label>

      {isError ? (
        <ErrorState detail="โหลดสรุปมาสายไม่สำเร็จ" onRetry={() => void refetch()} />
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState icon={Clock} title="ไม่มีคนมาสายในช่วงนี้" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium whitespace-nowrap">รหัส</th>
                <th className="px-2 py-2 font-medium whitespace-nowrap">ชื่อ</th>
                <th className="px-2 py-2 font-medium whitespace-nowrap">แผนก</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">จำนวนครั้ง</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">รวมเวลา</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">เฉลี่ยต่อครั้ง</th>
                <th className="px-2 py-2 font-medium text-right whitespace-nowrap">สายมากสุด</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                rows.map((r) => {
                  const avg = r.late_days > 0 ? r.late_minutes / r.late_days : 0;
                  return (
                    <tr
                      key={r.emp_code}
                      className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50"
                      onClick={() =>
                        navigate(`/hr/attendance?emp=${encodeURIComponent(r.emp_code)}&from=${range.from}&to=${range.to}`)
                      }
                    >
                      <td className="px-3 py-1.5 font-mono text-xs text-gray-500 whitespace-nowrap">{r.emp_code}</td>
                      <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">{r.name}</td>
                      <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{r.department ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{r.late_days}</td>
                      <td className="px-2 py-1.5 text-right font-semibold text-rose-700 whitespace-nowrap">{lateLabel(r.late_minutes)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">{r.late_days > 0 ? lateLabel(avg) : '—'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-500 whitespace-nowrap">
                        {r.worst_date ? `${formatShortDate(r.worst_date)} (${lateLabel(r.worst_minutes)})` : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
